// Shared required-field validation, extracted from the near-identical copies in
// permit_form.js, vehicles/form.js, code_enforcement/multi-step-form.js and
// ce_case_follow_ups/form.js. Use this instead of `required: true` so the error
// reads as a message under the field rather than a native browser bubble.
//
// MARKUP CONTRACT (unchanged from the implementations it replaces)
//   .requiredField      marks an input as required
//   data-error-message  the message to show for that field; falls back to the
//                       nearest ancestor carrying one, then to defaultMessage
//
// USAGE
//   FormValidator.attach('#vehicle-form');            // live + submit guard
//   FormValidator.attach('#vehicle-form', {           // everything is overridable
//     defaultMessage: 'Required to continue',
//     onInvalid: function (result, $form) { ... }     // e.g. open a collapsed panel
//   });
//
//   var result = FormValidator.validate($('#vehicle-form'));
//   // -> { isValid: false, firstErrorField: $input, invalidFields: [$input, ...] }
//
// The selector passed to attach() is delegated off document, so it keeps working
// for forms injected over AJAX (edit modals) without re-attaching.

(function (window, $) {
  'use strict';

  var DEFAULTS = {
    fieldSelector: '.requiredField',
    defaultMessage: 'This field is required',
    errorClass: 'error-message',
    invalidClasses: 'is-invalid error-field',
    liveValidate: true,
    focusFirstError: true,
    validateOnSubmit: true,

    // Bypass the whole form - for "save as draft" style buttons that accept an
    // incomplete record. Any errors already on screen are cleared.
    // skip: function ($form) { return $form.data('lastCommit') === 'Save as Draft'; }
    skip: null, // function ($scope) -> true to bypass

    // Form-level rules that do not belong to any single field - "at least one
    // section", "end date after start date", and so on. Keep them here, in the
    // form that needs them, rather than growing the shared logic.
    //
    //   rules: [function ($form) {
    //     if ($form.find('.section-card').length) return true;
    //     return { message: 'Add at least one section', target: '.sections-container' };
    //   }]
    //
    // Return true/undefined to pass; false to fail with defaultMessage; or
    // { message, target, placement } where placement is
    // append (default) | prepend | after | before.
    rules: [],

    // Hooks. isFieldValid/placeError return undefined (or false, for placeError)
    // to fall through to the built-in behaviour.
    isFieldValid: null, // function ($field) -> bool | undefined
    placeError: null,   // function ($field, $error) -> true when handled
    onInvalid: null     // function (result, $scope)
  };

  var EVENT_NS = '.formValidator';

  function settingsFor(options) {
    return $.extend({}, DEFAULTS, options || {});
  }

  function typeOf($field) {
    return ($field.attr('type') || '').toLowerCase();
  }

  function isBlank(value) {
    if (value === null || value === undefined) return true;
    if ($.isArray(value)) return value.length === 0;

    return String(value).trim() === '';
  }

  // ---------------------------------------------------------------- lookups

  // select2 hides the real <select>, so the error has to decorate its widget.
  function select2Container($field) {
    if (!$field.hasClass('select2-hidden-accessible')) return $();

    var $container = $field.siblings('.select2-container');

    return $container.length ? $container : $field.parent().find('.select2-container');
  }

  // Covers both upload styles in use: the dropzone-ish areas that render
  // .file-preview nodes, and the plainer wrappers around a file input.
  function uploadArea($field) {
    if (typeOf($field) !== 'file') return $();

    var $area = $field.closest('div[data-target="upload-form"]');

    return $area.length ? $area : $field.closest('.upload-form, .file-upload-area, div[data-handlers-initialized]');
  }

  function radioGroup($field) {
    var $group = $field.closest('[data-radio-group]');
    if ($group.length) return $group;

    // Fall back to name-based grouping when the wrapper is absent. Rails names
    // contain brackets, so filter rather than build an attribute selector.
    var name = $field.attr('name');
    if (!name) return $field;

    var $form = $field.closest('form');
    var $scope = $form.length ? $form : $(document);

    return $scope.find('input[type="radio"]').filter(function () {
      return $(this).attr('name') === name;
    });
  }

  // The elements that should look invalid - the field itself plus whichever
  // widget is standing in for it.
  function decorationTargets($field) {
    var $targets = $field;

    var $select2 = select2Container($field);
    if ($select2.length) $targets = $targets.add($select2).add($select2.find('.select2-selection'));

    var $multiselect = $field.closest('.custom-multiselect').find('.select-field-container');
    if ($multiselect.length) $targets = $targets.add($multiselect);

    var $upload = uploadArea($field);
    if ($upload.length) $targets = $targets.add($upload);

    return $targets;
  }

  function messageFor($field, settings) {
    var own = $field.attr('data-error-message');
    if (own) return own;

    var inherited = $field.closest('[data-error-message]').attr('data-error-message');

    return inherited || settings.defaultMessage;
  }

  // ------------------------------------------------------------- exclusions

  // A row the user removed via cocoon still sits in the DOM with _destroy set;
  // its inputs are not submitted, so requiring them would deadlock the form.
  function markedForDestruction($field) {
    var $nested = $field.closest('.nested-fields');
    if (!$nested.length) return false;

    return $nested.find('input[name$="[_destroy]"]').filter(function () {
      if ($(this).closest('.nested-fields')[0] !== $nested[0]) return false;

      var value = String($(this).val()).toLowerCase();

      return value === '1' || value === 'true';
    }).length > 0;
  }

  // Fields inside a collapsed panel ARE still validated on purpose - reporting
  // those is exactly what native validation cannot do. type="hidden" is the
  // different case: the user cannot fill it and an error beside it would be
  // invisible. Skipping it also stops flatpickr's pair being validated twice -
  // flatpickr turns the original input into type=hidden and copies its classes
  // (requiredField included) onto the visible altInput it inserts alongside.
  // A hidden value that must be present belongs in a rule, not a field marker.
  function skipField($field) {
    return $field.is(':disabled') ||
      $field.is('input[type="hidden"]') ||
      markedForDestruction($field);
  }

  // ------------------------------------------------------------- validation

  function fieldIsValid($field, settings) {
    if (settings.isFieldValid) {
      var custom = settings.isFieldValid($field);
      if (custom !== undefined) return custom;
    }

    var type = typeOf($field);

    if (type === 'file') {
      // Uploads already held server side, or previewed client side, both count.
      if ($field.attr('data-files-present') === 'true') return true;
      if (!isBlank($field.val())) return true;

      return uploadArea($field).find('.file-preview').length > 0;
    }

    if (type === 'radio') return radioGroup($field).is(':checked');
    if (type === 'checkbox') return $field.is(':checked');

    return !isBlank($field.val());
  }

  function placeError($field, $error, settings) {
    if (settings.placeError && settings.placeError($field, $error) === true) return;

    var $explicit = $field.closest('[data-error-container]');
    if ($explicit.length) return $explicit.append($error);

    if (typeOf($field) === 'radio') {
      var $group = $field.closest('[data-radio-group]');
      if ($group.length) return $group.after($error);
    }

    // Wrappers that draw a prefix/suffix around the input: the message belongs
    // under the whole control, not between it and its unit.
    if ($field.parent().hasClass('input-wrapper')) return $field.parent().after($error);

    var $select2 = select2Container($field);
    if ($select2.length) return $select2.after($error);

    var $upload = uploadArea($field);
    if ($upload.length) return $upload.after($error);

    var $formField = $field.closest('.form-field');
    if ($formField.length) return $formField.append($error);

    $field.after($error);
  }

  // .text() rather than string concatenation - messages come from data
  // attributes and rule callbacks, and must never be parsed as markup.
  function buildError(message, settings) {
    return $('<div></div>')
      .addClass(settings.errorClass)
      .addClass('text-danger small mt-1')
      .text(message);
  }

  function showError($field, settings) {
    decorationTargets($field).addClass(settings.invalidClasses);

    var $error = buildError(messageFor($field, settings), settings);

    placeError($field, $error, settings);
    $field.data('formValidatorError', $error);
  }

  function clearField($field, settings) {
    decorationTargets($field).removeClass(settings.invalidClasses);

    var $existing = $field.data('formValidatorError');
    if ($existing) {
      $existing.remove();
      $field.removeData('formValidatorError');
    }
  }

  function clear($scope, options) {
    var settings = settingsFor(options);
    var $target = $($scope);

    $target.find(settings.fieldSelector).each(function () {
      clearField($(this), settings);
    });

    // Sweep anything left behind by an earlier run or a re-render.
    $target.find('.' + settings.errorClass).remove();
  }

  function focusField($field) {
    if (!$field || !$field.length) return;

    if ($field.is(':visible') && $field.is('input, select, textarea, button, [tabindex]')) {
      return $field.trigger('focus');
    }

    // Not focusable, or inside something collapsed - surface it as best we can
    // and let the caller open the container from onInvalid.
    var $visible = $field.is(':visible') ? $field : $field.closest(':visible');
    if ($visible[0] && $visible[0].scrollIntoView) $visible[0].scrollIntoView({ block: 'center' });
  }

  // rails-ujs disables submit buttons from a 13ms timer that fires even when the
  // submit was prevented, and only re-enables on ajax:complete/ajax:stopped -
  // which never arrive for a blocked form. Undo it after that timer has run.
  function releaseSubmitLock($form) {
    window.setTimeout(function () {
      if (window.Rails && window.Rails.enableElement) return window.Rails.enableElement($form[0]);

      $form.find('input[type="submit"], button[type="submit"]').prop('disabled', false);
    }, 30);
  }

  function runRules($scope, settings, result) {
    $.each(settings.rules || [], function (_index, rule) {
      var outcome = rule($scope, result);
      if (outcome === true || outcome === undefined || outcome === null) return;

      var spec = (outcome === false) ? {} : outcome;
      if (spec.valid === true) return;

      var $target = $scope;
      if (spec.target) {
        $target = (typeof spec.target === 'string') ? $scope.find(spec.target) : $(spec.target);
        if (!$target.length) $target = $scope;
      }

      var $error = buildError(spec.message || settings.defaultMessage, settings);
      var placement = spec.placement || 'append';

      if (placement === 'after') $target.after($error);
      else if (placement === 'before') $target.before($error);
      else if (placement === 'prepend') $target.prepend($error);
      else $target.append($error);

      result.isValid = false;
      result.ruleErrors.push({ target: $target, message: $error.text() });
      result.firstErrorField = result.firstErrorField || $target;
    });
  }

  function validate($scope, options) {
    var settings = settingsFor(options);
    var $target = $($scope);
    var result = { isValid: true, firstErrorField: null, invalidFields: [], ruleErrors: [], skipped: false };
    var reportedRadioNames = [];

    clear($target, settings);

    if (settings.skip && settings.skip($target) === true) {
      result.skipped = true;

      return result;
    }

    $target.find(settings.fieldSelector).each(function () {
      var $field = $(this);
      if (skipField($field)) return;

      // One message per radio group, not one per button.
      if (typeOf($field) === 'radio') {
        var name = $field.attr('name');
        if (name && reportedRadioNames.indexOf(name) !== -1) return;
        if (name) reportedRadioNames.push(name);
      }

      if (fieldIsValid($field, settings)) return;

      result.isValid = false;
      result.firstErrorField = result.firstErrorField || $field;
      result.invalidFields.push($field);
      showError($field, settings);
    });

    runRules($target, settings, result);

    if (!result.isValid && settings.onInvalid) settings.onInvalid(result, $target);

    return result;
  }

  function validateField($field, options) {
    var settings = settingsFor(options);

    clearField($field, settings);

    if (skipField($field) || fieldIsValid($field, settings)) return true;

    showError($field, settings);

    return false;
  }

  // ----------------------------------------------------------------- wiring

  function attach(scopeSelector, options) {
    var settings = settingsFor(options);
    var fieldSelector = scopeSelector + ' ' + settings.fieldSelector;
    var $document = $(document);

    if (settings.liveValidate) {
      $document
        .off('blur' + EVENT_NS, fieldSelector)
        .on('blur' + EVENT_NS, fieldSelector, function () {
          validateField($(this), settings);
        });

      // change covers selects, checkboxes, radios and file inputs, which never
      // usefully blur.
      $document
        .off('change' + EVENT_NS, fieldSelector)
        .on('change' + EVENT_NS, fieldSelector, function () {
          validateField($(this), settings);
        });
    }

    if (settings.validateOnSubmit) {
      $document
        .off('submit' + EVENT_NS, scopeSelector)
        .on('submit' + EVENT_NS, scopeSelector, function (event) {
          var $form = $(this);
          var result = validate($form, settings);
          if (result.isValid) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          releaseSubmitLock($form);

          if (settings.focusFirstError && result.firstErrorField) focusField(result.firstErrorField);
        });

      // rails-ujs binds its own submit handler before this one, so remote forms
      // need cancelling on their own event.
      $document
        .off('ajax:before' + EVENT_NS, scopeSelector)
        .on('ajax:before' + EVENT_NS, scopeSelector, function () {
          var $form = $(this);
          var result = validate($form, settings);
          if (result.isValid) return true;

          releaseSubmitLock($form);

          if (settings.focusFirstError && result.firstErrorField) focusField(result.firstErrorField);

          return false;
        });
    }
  }

  function detach(scopeSelector, options) {
    var settings = settingsFor(options);
    var $document = $(document);

    $document.off('blur' + EVENT_NS, scopeSelector + ' ' + settings.fieldSelector);
    $document.off('change' + EVENT_NS, scopeSelector + ' ' + settings.fieldSelector);
    $document.off('submit' + EVENT_NS, scopeSelector);
    $document.off('ajax:before' + EVENT_NS, scopeSelector);
  }

  window.FormValidator = {
    attach: attach,
    detach: detach,
    validate: validate,
    validateField: validateField,
    clear: clear,
    // Exposed so custom rules can filter out cocoon-removed rows without
    // reimplementing the _destroy check.
    isMarkedForDestruction: markedForDestruction,
    defaults: DEFAULTS
  };
})(window, jQuery);
